import React from 'react';
import Box from '@mui/material/Box';
import {
    Button, Checkbox, Chip, Collapse, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Divider, IconButton,
    Menu, MenuItem as MuiMenuItem, Paper, Popover, Select, TextField,
    ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import TableChartIcon from '@mui/icons-material/TableChart';
import MapIcon from '@mui/icons-material/Map';
import LayersIcon from '@mui/icons-material/Layers';
import ApartmentIcon from '@mui/icons-material/Apartment';

import {GEOJSON_EXPORT_MODES} from '../utils/geoJsonUtils';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArticleIcon from '@mui/icons-material/Article';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StorageIcon from '@mui/icons-material/Storage';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EjectIcon from '@mui/icons-material/Eject';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
// ── Champs identifiants (clés de jointure) ───────────────────────────────────
const KEY_FIELDS = [
    { suffix: 'cle_interop_adr',          label: 'Clé interop. adresse',    shortLabel: 'Clé ADR' },
    { suffix: 'batiment_construction_id', label: 'ID construction bâtiment', shortLabel: 'Constr.' },
    { suffix: 'batiment_groupe_id',       label: 'ID groupe bâtiment',       shortLabel: 'Groupe'  },
    { suffix: 'parcelle_id',              label: 'ID parcelle',              shortLabel: 'Parcelle'},
];

// ── Labels des séparateurs ────────────────────────────────────────────────────
const SEP_OPTIONS = [
    { value: ';',   label: ';',  title: 'Point-virgule (Excel FR)' },
    { value: ',',   label: ',',  title: 'Virgule (Excel EN / CSV standard)' },
    { value: '\\t', label: '⇥',  title: 'Tabulation (TSV)' },
    { value: '|',   label: '|',  title: 'Pipe' },
];

// ── Labels du séparateur de liste (valeurs multiples dans une cellule) ────────
const LIST_SEP_OPTIONS = [
    { value: ' | ',  label: '|',  title: 'Pipe  →  valeur1 | valeur2' },
    { value: ' - ',  label: '-',  title: 'Tiret  →  valeur1 - valeur2' },
    { value: '\n',   label: '↵',  title: 'Saut de ligne (char 10)' },
    { value: ' || ', label: '||', title: 'Double pipe  →  valeur1 || valeur2' },
    { value: ' & ',  label: '&',  title: 'Esperluette  →  valeur1 & valeur2' },
];

// ── Style partagé boutons outlined ────────────────────────────────────────────
const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.67rem',
    height: 24,
    borderColor: 'rgba(25,118,210,0.25)',
    '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(25,118,210,0.06)' },
    '&.Mui-disabled': { borderColor: 'rgba(0,0,0,0.1)' },
};

// ── Section pill wrapper ──────────────────────────────────────────────────────
const sectionBoxSx = {
    display: 'flex', alignItems: 'center', gap: 0.5,
    bgcolor: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 2.5,
    px: 0.75, py: 0.2,
    backdropFilter: 'blur(4px)',
};

/**
 * Barre d'outils principale : saisie de la localisation, bouton de chargement,
 * sélecteur de sources/colonnes, export CSV et sélecteur de séparateur.
 */
export default function DataToolbar({
    localisation, setLocalisation,
    typeLoc, setTypeLoc,
    loading, csvData,
    csvSeparator, setCsvSeparator,
    listSeparator, setListSeparator,
    onLoad, onExport, onExportGeoJson,
    onCancel,
    onSaveAs, onSave, onOpen, onLoadConfig, onDeleteConfig, onUnloadConfig,
    savedConfigs = [],
    currentConfigName,
    isDirty,
    availableSources,
    columnVisibilityModel, setColumnVisibilityModel,
    rowCount, filteredRowCount,
    viewMode, onViewModeChange,
    geoMode, onGeoModeChange,
    codeProgress,  // null | { idx: number, total: number }
    sourcesWithState = [], // pour afficher la volumétrie en cours de chargement
}) {
    const [sourcesAnchor, setSourcesAnchor] = React.useState(null);
    const [csvMenuAnchor, setCsvMenuAnchor] = React.useState(null);
    const [fileMenuAnchor, setFileMenuAnchor] = React.useState(null);
    const [openSubMenuAnchor, setOpenSubMenuAnchor] = React.useState(null);
    const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
    const [saveDialogName, setSaveDialogName] = React.useState('');
    const ouvrirItemRef = React.useRef(null);
    const [expandedGroups, setExpandedGroups] = React.useState(new Set());
    const fileInputRef = React.useRef(null);


    const isColVisible = (field) => columnVisibilityModel[field] !== false;

    const groupStats = (s) => {
        const total = s.columns.length;
        const visible = s.columns.filter(c => isColVisible(c.field)).length;
        return {total, visible, all: visible === total, none: visible === 0};
    };

    const toggleGroup = (s, checked) => {
        const next = {...columnVisibilityModel};
        s.columns.forEach(c => { next[c.field] = checked; });
        setColumnVisibilityModel(next);
    };

    const toggleCol = (field, checked) => {
        setColumnVisibilityModel({...columnVisibilityModel, [field]: checked});
    };

    const selectAll = () => {
        const next = {...columnVisibilityModel};
        availableSources.forEach(s => s.columns.forEach(c => { next[c.field] = true; }));
        setColumnVisibilityModel(next);
    };

    const deselectAll = () => {
        const next = {...columnVisibilityModel};
        availableSources.forEach(s => s.columns.forEach(c => { next[c.field] = false; }));
        setColumnVisibilityModel(next);
    };

    // ── Helper : trouver un champ par son suffixe ─────────────────────────────
    const findFieldBySuffix = React.useCallback((suffix) => {
        for (const s of availableSources) {
            for (const col of s.columns) {
                if (col.field === suffix || col.field.endsWith('__' + suffix)) return col.field;
            }
        }
        return null;
    }, [availableSources]);

    // ── Compteurs bouton ──────────────────────────────────────────────────────
    const visibleGroupCount = availableSources.filter(s => !groupStats(s).none).length;
    const totalGroupCount   = availableSources.length;
    const anyHidden         = availableSources.some(s => !groupStats(s).all);
    const anyVisible        = availableSources.some(s => !groupStats(s).none);

    const isFiltered = csvData && filteredRowCount !== rowCount;

    // ── Total des enregistrements chargés (toutes sources cumulées) ──────────
    const totalLoadedCount = sourcesWithState.reduce((acc, s) => acc + (s.count ?? 0), 0);

    return (
        <>
        <Paper
            elevation={0}
            sx={{
                mx: 1, mt: 1, px: 1.5, py: 0.6,
                borderRadius: 3,
                display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap',
                background: 'linear-gradient(160deg, #fafcff 0%, #eef3fb 100%)',
                border: '1px solid rgba(25,118,210,0.15)',
                borderTop: '3px solid',
                borderTopColor: 'primary.main',
                boxShadow: '0 2px 18px rgba(25,118,210,0.08), 0 1px 4px rgba(0,0,0,0.04)',
                position: 'sticky', top: 8, zIndex: 10,
            }}
        >
            {/* ── Menu Fichier ──────────────────────────────────────────────── */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={e => {
                    const file = e.target.files?.[0];
                    if (file && onOpen) onOpen(file);
                    e.target.value = '';
                    setFileMenuAnchor(null);
                }}
            />
            <Button
                variant="outlined"
                size="small"
                startIcon={<ArticleIcon sx={{ fontSize: '0.9rem !important' }} />}
                endIcon={<ExpandMoreIcon sx={{ fontSize: '0.75rem !important', ml: -0.5 }} />}
                onClick={e => setFileMenuAnchor(e.currentTarget)}
                sx={{
                    ...outlinedBtnSx,
                    px: 1.2,
                    minWidth: 0,
                }}
            >
                Fichier
            </Button>
            <Menu
                anchorEl={fileMenuAnchor}
                open={Boolean(fileMenuAnchor)}
                onClose={() => setFileMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        elevation: 6,
                        sx: {
                            borderRadius: 2.5, mt: 0.5, minWidth: 200,
                            border: '1px solid rgba(25,118,210,0.12)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.13)',
                        },
                    },
                }}
            >
                <MuiMenuItem
                    ref={ouvrirItemRef}
                    onMouseEnter={() => setOpenSubMenuAnchor(ouvrirItemRef.current)}
                    onMouseLeave={(e) => {
                        // Ne ferme que si la souris ne va pas vers le sous-menu
                        if (!e.relatedTarget?.closest?.('[data-submenu]')) {
                            setOpenSubMenuAnchor(null);
                        }
                    }}
                    sx={{ gap: 1.5, fontSize: '0.82rem', py: 1, justifyContent: 'space-between', pr: 1 }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FolderOpenIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                        Ouvrir
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: '1rem', color: 'text.secondary', ml: 2 }} />
                </MuiMenuItem>

                {/* ── Sous-menu Ouvrir ── */}
                <Menu
                    data-submenu
                    anchorEl={openSubMenuAnchor}
                    open={Boolean(openSubMenuAnchor)}
                    onClose={() => setOpenSubMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            'data-submenu': true,
                            elevation: 8,
                            onMouseLeave: () => setOpenSubMenuAnchor(null),
                            sx: {
                                borderRadius: 2.5, minWidth: 220,
                                border: '1px solid rgba(25,118,210,0.12)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                ml: 0.5,
                            },
                        },
                    }}
                >
                    {savedConfigs.length === 0 && (
                        <MuiMenuItem disabled sx={{ fontSize: '0.78rem', color: 'text.disabled', fontStyle: 'italic' }}>
                            Aucune configuration sauvegardée
                        </MuiMenuItem>
                    )}
                    {savedConfigs.map(cfg => (
                        <MuiMenuItem
                            key={cfg.name}
                            onClick={() => { onLoadConfig?.(cfg.name); setOpenSubMenuAnchor(null); setFileMenuAnchor(null); }}
                            sx={{
                                gap: 1, fontSize: '0.82rem', py: 0.75, pr: 0.5,
                                bgcolor: cfg.name === currentConfigName ? 'rgba(25,118,210,0.07)' : undefined,
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: cfg.name === currentConfigName ? 700 : 600,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    color: cfg.name === currentConfigName ? 'primary.main' : 'text.primary' }}>
                                    {cfg.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
                                    {new Date(cfg.savedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                                </Typography>
                            </Box>
                            {cfg.name === currentConfigName ? (
                                <CheckIcon sx={{ fontSize: '0.9rem', color: 'primary.main', flexShrink: 0, ml: 0.5 }} />
                            ) : (
                                <Tooltip title="Supprimer cette configuration" placement="right">
                                    <IconButton
                                        size="small"
                                        onClick={e => { e.stopPropagation(); onDeleteConfig?.(cfg.name); }}
                                        sx={{
                                            ml: 0.5, flexShrink: 0, width: 22, height: 22,
                                            color: 'error.light',
                                            '&:hover': { bgcolor: 'rgba(211,47,47,0.1)', color: 'error.main' },
                                        }}
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MuiMenuItem>
                    ))}
                    {savedConfigs.length > 0 && <Divider sx={{ my: 0.5, opacity: 0.6 }} />}
                    <MuiMenuItem
                        onClick={() => {
                            fileInputRef.current?.click();
                            setOpenSubMenuAnchor(null);
                        }}
                        sx={{ gap: 1.5, fontSize: '0.82rem', py: 1 }}
                    >
                        <FolderOpenIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                        Depuis le disque…
                    </MuiMenuItem>
                </Menu>
                <MuiMenuItem
                    onClick={() => { onSave?.(); setFileMenuAnchor(null); }}
                    disabled={!currentConfigName}
                    sx={{ gap: 1.5, fontSize: '0.82rem', py: 1 }}
                >
                    <SaveIcon sx={{ fontSize: '1rem', color: currentConfigName ? 'primary.main' : 'text.disabled' }} />
                    <Box sx={{ flex: 1 }}>Enregistrer</Box>
                    {currentConfigName && (
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', pl: 1, whiteSpace: 'nowrap', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {currentConfigName}
                        </Typography>
                    )}
                </MuiMenuItem>
                <MuiMenuItem
                    onClick={() => {
                        setSaveDialogName(currentConfigName ?? '');
                        setSaveDialogOpen(true);
                        setFileMenuAnchor(null);
                    }}
                    sx={{ gap: 1.5, fontSize: '0.82rem', py: 1 }}
                >
                    <SaveIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    Enregistrer sous…
                </MuiMenuItem>
                <Divider sx={{ my: 0.5, opacity: 0.6 }} />
                <MuiMenuItem
                    onClick={() => { onUnloadConfig?.(); setFileMenuAnchor(null); }}
                    disabled={!currentConfigName}
                    sx={{ gap: 1.5, fontSize: '0.82rem', py: 1, color: currentConfigName ? 'text.secondary' : 'text.disabled' }}
                >
                    <EjectIcon sx={{ fontSize: '1rem', color: currentConfigName ? 'text.secondary' : 'text.disabled' }} />
                    Décharger la configuration
                </MuiMenuItem>
            </Menu>

            {/* ── Nom config courante (indicateur) ─────────────────────────── */}
            {currentConfigName && (
                <Tooltip title={isDirty ? 'Configuration modifiée – non enregistrée' : 'Configuration chargée'}>
                    <Chip
                        icon={<ArticleIcon sx={{ fontSize: '0.78rem !important' }} />}
                        label={currentConfigName}
                        size="small"
                        variant="outlined"
                        color={isDirty ? 'warning' : 'primary'}
                        sx={{ fontWeight: 700, fontSize: '0.72rem', height: 22, maxWidth: 180,
                              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                    />
                </Tooltip>
            )}

            <Divider orientation="vertical" flexItem sx={{mx: 0.25, opacity: 0.35}}/>

            {/* ── Section recherche ─────────────────────────────────────────── */}
            <Box sx={{
                ...sectionBoxSx,
                bgcolor: 'rgba(25,118,210,0.04)',
                border: '1px solid rgba(25,118,210,0.14)',
            }}>
                <Tooltip title="Saisissez un ou plusieurs codes séparés par ; (ex : 34274;13055)">
                    <TextField
                        label="Code(s) localisation" size="small" disabled={loading}
                        value={localisation} onChange={e => setLocalisation(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && !loading && onLoad()}
                        placeholder="code1;code2;…"
                        sx={{
                            width: 210,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2, fontSize: '0.82rem', bgcolor: '#fff',
                                '&.Mui-focused fieldset': {borderWidth: 1.5},
                            },
                            '& .MuiInputLabel-root': {fontSize: '0.78rem'},
                        }}
                    />
                </Tooltip>

                {/* Indicateur de progression multi-codes */}
                {codeProgress && (
                    <Chip
                        label={`${codeProgress.idx}/${codeProgress.total}`}
                        size="small"
                        color="info"
                        variant="filled"
                        sx={{
                            fontWeight: 700, fontSize: '0.72rem', height: 22,
                            boxShadow: '0 2px 8px rgba(2,136,209,0.3)',
                        }}
                    />
                )}

                <Select size="small" disabled={loading}
                        value={typeLoc} onChange={e => setTypeLoc(e.target.value)}
                        sx={{
                            width: 155, borderRadius: 2, fontSize: '0.82rem', bgcolor: '#fff',
                            '& .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(0,0,0,0.15)'},
                        }}
                >
                    <MuiMenuItem value="commune">Commune</MuiMenuItem>
                    <MuiMenuItem value="epci" >EPCI</MuiMenuItem>
                </Select>

                <Button
                    variant="contained" disabled={loading}
                    startIcon={loading
                        ? <CircularProgress size={13} sx={{color: 'rgba(255,255,255,0.8)'}}/>
                        : <SearchIcon sx={{fontSize: '1rem !important'}}/>
                    }
                    onClick={onLoad}
                    sx={{
                        borderRadius: 2, textTransform: 'none',
                        fontWeight: 700, fontSize: '0.82rem',
                        height: 36, px: 2.2, minWidth: 105,
                        background: loading
                            ? undefined
                            : 'linear-gradient(135deg, #1976d2 0%, #1251a3 100%)',
                        boxShadow: loading ? 'none' : '0 2px 10px rgba(25,118,210,0.45)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                            boxShadow: '0 4px 16px rgba(25,118,210,0.5)',
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                    }}
                >
                    {loading ? 'Chargement…' : 'Charger'}
                </Button>

                {/* ── Bouton Annuler (visible seulement pendant le chargement) ── */}
                {loading && (
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={onCancel}
                        startIcon={<CancelIcon sx={{ fontSize: '0.9rem !important' }} />}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            height: 36,
                            px: 1.8,
                            borderColor: 'rgba(211,47,47,0.4)',
                            color: 'error.main',
                            bgcolor: 'rgba(211,47,47,0.04)',
                            '&:hover': {
                                borderColor: 'error.main',
                                bgcolor: 'rgba(211,47,47,0.10)',
                                boxShadow: '0 2px 8px rgba(211,47,47,0.25)',
                            },
                            transition: 'all 0.18s ease',
                        }}
                    >
                        Annuler
                    </Button>
                )}
            </Box>

            {/* ── Chip compteur / volumétrie ───────────────────────────────── */}
            {csvData && rowCount > 0 && (
                <Tooltip title={isFiltered
                    ? `${rowCount - filteredRowCount} bâtiment${rowCount - filteredRowCount > 1 ? 's' : ''} masqué${rowCount - filteredRowCount > 1 ? 's' : ''} par les filtres actifs`
                    : `${rowCount.toLocaleString('fr-FR')} bâtiment${rowCount > 1 ? 's' : ''} chargé${rowCount > 1 ? 's' : ''}`
                }>
                    <Chip
                        icon={<ApartmentIcon sx={{fontSize: '0.85rem !important', ml: '6px !important'}}/>}
                        label={isFiltered
                            ? `${filteredRowCount.toLocaleString('fr-FR')} / ${rowCount.toLocaleString('fr-FR')}`
                            : `${rowCount.toLocaleString('fr-FR')} bât.`
                        }
                        size="small"
                        color={isFiltered ? 'warning' : 'primary'}
                        variant="filled"
                        sx={{
                            fontWeight: 700, fontSize: '0.76rem', height: 26,
                            boxShadow: isFiltered
                                ? '0 2px 8px rgba(237,108,2,0.35)'
                                : '0 2px 8px rgba(25,118,210,0.3)',
                            '& .MuiChip-label': {pr: 1},
                        }}
                    />
                </Tooltip>
            )}

            <Divider sx={{mb: 0.75, opacity: 0.6}}/>


            <Tooltip title={!csvData ? 'Chargez des données pour filtrer les colonnes' : 'Choisir les sources et colonnes à afficher'}>
                <span>
                    <Button
                        variant={anyHidden && anyVisible ? 'contained' : 'outlined'}
                        color={anyHidden && anyVisible ? 'warning' : 'primary'}
                        disabled={!csvData || totalGroupCount === 0}
                        startIcon={<FilterListIcon sx={{fontSize: '0.9rem !important'}}/>}
                        onClick={e => setSourcesAnchor(e.currentTarget)}
                        sx={{
                            ...outlinedBtnSx,
                            ...(anyHidden && anyVisible ? {
                                boxShadow: '0 2px 8px rgba(237,108,2,0.35)',
                                textTransform: 'none',
                            } : {}),
                        }}
                    >
                        Sources{csvData ? ` ${visibleGroupCount}/${totalGroupCount}` : ''}
                    </Button>
                </span>
            </Tooltip>

            <Popover
                open={Boolean(sourcesAnchor)}
                anchorEl={sourcesAnchor}
                onClose={() => setSourcesAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                slotProps={{paper: {
                    elevation: 8,
                    sx: {
                        borderRadius: 3, mt: 0.75, minWidth: 300,
                        maxHeight: '72vh', overflowY: 'auto',
                        border: '1px solid rgba(25,118,210,0.12)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    },
                }}}
            >
                <Box sx={{p: 1.5}}>
                    {/* En-tête popover */}
                    <Box sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        mb: 1, pb: 0.75,
                    }}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                            <FilterListIcon sx={{fontSize: '0.9rem', color: 'primary.main'}}/>
                            <Typography sx={{
                                fontWeight: 700, color: 'text.primary',
                                fontSize: '0.75rem', letterSpacing: '0.02em',
                            }}>
                                Sources &amp; colonnes
                            </Typography>
                        </Box>
                        <Box sx={{display: 'flex', gap: 0.5}}>
                            <Tooltip title="Tout sélectionner" placement="top">
                                <IconButton size="small" onClick={selectAll} color="primary"
                                            sx={{
                                                bgcolor: 'rgba(25,118,210,0.08)',
                                                '&:hover': {bgcolor: 'rgba(25,118,210,0.18)'},
                                                width: 26, height: 26,
                                            }}>
                                    <DoneAllIcon sx={{fontSize: '0.9rem'}}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Tout désélectionner" placement="top">
                                <IconButton size="small" onClick={deselectAll} color="error"
                                            sx={{
                                                bgcolor: 'rgba(211,47,47,0.08)',
                                                '&:hover': {bgcolor: 'rgba(211,47,47,0.18)'},
                                                width: 26, height: 26,
                                            }}>
                                    <RemoveCircleOutlineIcon sx={{fontSize: '0.9rem'}}/>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Divider sx={{mb: 0.75, opacity: 0.6}}/>

                    {availableSources.map(s => {
                        const {total, visible, all, none} = groupStats(s);
                        const indeterminate = !all && !none;
                        const expanded = expandedGroups.has(s.key);

                        return (
                            <Box key={s.key}>
                                {/* ── Ligne groupe ── */}
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5, py: 0.4,
                                    borderRadius: 1.5, px: 0.5,
                                    transition: 'background 0.15s',
                                    '&:hover': {bgcolor: `${s.color}0d`},
                                }}>
                                    <Checkbox
                                        size="small"
                                        checked={all}
                                        indeterminate={indeterminate}
                                        onChange={e => toggleGroup(s, e.target.checked)}
                                        sx={{'& .MuiSvgIcon-root': {fontSize: 18, color: s.color}}}
                                    />
                                    <Box sx={{
                                        width: 9, height: 9, borderRadius: '50%',
                                        bgcolor: s.color, flexShrink: 0,
                                        boxShadow: `0 0 0 2.5px ${s.color}44`,
                                    }}/>
                                    <Typography variant="body2" sx={{ml: 0.5, flex: 1, fontWeight: 600, fontSize: '0.8rem'}}>
                                        {s.label}
                                    </Typography>
                                    <Chip
                                        label={`${visible}/${total}`}
                                        size="small"
                                        sx={{
                                            height: 18, fontSize: '0.63rem', fontWeight: 700,
                                            bgcolor: none ? 'grey.100' : `${s.color}1a`,
                                            color: none ? 'text.disabled' : s.color,
                                            border: `1px solid ${s.color}55`,
                                            '& .MuiChip-label': {px: 0.75},
                                        }}
                                    />
                                    <IconButton size="small" onClick={() => {
                                        const next = new Set(expandedGroups);
                                        expanded ? next.delete(s.key) : next.add(s.key);
                                        setExpandedGroups(next);
                                    }} sx={{ml: 0.25, color: 'text.secondary'}}>
                                        {expanded ? <ExpandLessIcon sx={{fontSize: '1rem'}}/> : <ExpandMoreIcon sx={{fontSize: '1rem'}}/>}
                                    </IconButton>
                                </Box>

                                {/* ── Colonnes individuelles ── */}
                                <Collapse in={expanded} unmountOnExit>
                                    <Box sx={{
                                        pl: 1.5, pb: 0.5, ml: 3.5,
                                        borderLeft: `2px solid ${s.color}44`,
                                        mt: 0.3,
                                    }}>
                                        {s.columns.map(col => (
                                            <Box key={col.field} sx={{display: 'flex', alignItems: 'center', gap: 0.5, py: 0.15}}>
                                                <Checkbox
                                                    size="small"
                                                    checked={isColVisible(col.field)}
                                                    onChange={e => toggleCol(col.field, e.target.checked)}
                                                    sx={{'& .MuiSvgIcon-root': {fontSize: 15, color: s.color + 'bb'}}}
                                                />
                                                <Typography variant="caption" sx={{
                                                    color: 'text.secondary',
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.7rem',
                                                }}>
                                                    {col.headerName}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Collapse>

                                <Divider sx={{my: 0.4, opacity: 0.35}}/>
                            </Box>
                        );
                    })}
                </Box>
            </Popover>

            <Divider orientation="vertical" flexItem sx={{mx: 0.25, opacity: 0.35}}/>

            {/* ── Groupe export ─────────────────────────────────────────────── */}
            <Box sx={{...sectionBoxSx, gap: 0.75}}>
                <Typography sx={{
                    fontSize: '0.58rem', fontWeight: 800, color: 'text.disabled',
                    textTransform: 'uppercase', letterSpacing: '0.14em', mr: 0.5, whiteSpace: 'nowrap',
                }}>
                    Export
                </Typography>

                <Tooltip title={!csvData ? 'Aucune donnée à exporter' : 'Télécharger en CSV (colonnes visibles)'}>
                    <span>
                        <Button variant="outlined"
                                disabled={!csvData || !anyVisible}
                                startIcon={<DownloadIcon sx={{fontSize: '0.9rem !important'}}/>}
                                onClick={onExport}
                                sx={outlinedBtnSx}
                        >
                            CSV
                        </Button>
                    </span>
                </Tooltip>

                <Tooltip title={!csvData ? 'Aucune donnée à exporter' : `GeoJSON – ${GEOJSON_EXPORT_MODES.find(m => m.key === geoMode)?.label ?? geoMode}`}>
                    <span>
                        <Button variant="outlined" color="success"
                                disabled={!csvData || !anyVisible}
                                startIcon={<FolderZipIcon sx={{fontSize: '0.9rem !important'}}/>}
                                onClick={() => onExportGeoJson(geoMode)}
                                sx={{
                                    ...outlinedBtnSx,
                                    borderColor: 'rgba(46,125,50,0.3)',
                                    color: 'success.dark',
                                    '&:hover': {borderColor: 'success.main', bgcolor: 'rgba(46,125,50,0.05)'},
                                }}
                        >
                            GeoJSON
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            <Divider orientation="vertical" flexItem sx={{mx: 0.25, opacity: 0.35}}/>

            {/* ── Sélecteur de mode géo ─────────────────────────────────────── */}
            <Tooltip title="Géométrie affichée sur la carte et utilisée pour l'export GeoJSON">
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                    <LayersIcon sx={{color: 'text.secondary', fontSize: '1rem', flexShrink: 0}}/>
                    <Select
                        size="small"
                        value={geoMode}
                        onChange={e => onGeoModeChange(e.target.value)}
                        disabled={!csvData}
                        renderValue={val => {
                            const m = GEOJSON_EXPORT_MODES.find(x => x.key === val);
                            return (
                                <Typography sx={{fontSize: '0.72rem', fontWeight: 600, maxWidth: 165, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                    {m?.label ?? val}
                                </Typography>
                            );
                        }}
                        sx={{
                            height: 26, borderRadius: 2, bgcolor: '#fff',
                            '& .MuiSelect-select': {py: 0, px: 1},
                            minWidth: 160,
                            '& .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(0,0,0,0.15)'},
                        }}
                    >
                        {GEOJSON_EXPORT_MODES.map(({key, label, description}) => (
                            <MuiMenuItem key={key} value={key}>
                                <Box>
                                    <Typography sx={{fontSize: '0.78rem', fontWeight: 600}}>{label}</Typography>
                                    <Typography variant="caption" sx={{color: 'text.secondary', fontSize: '0.65rem'}}>{description}</Typography>
                                </Box>
                            </MuiMenuItem>
                        ))}
                    </Select>
                </Box>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{mx: 0.25, opacity: 0.35}}/>

            {/* ── Toggle Tableau / Carte ────────────────────────────────────── */}
            <ToggleButtonGroup
                size="small" exclusive value={viewMode}
                onChange={(_, val) => { if (val) onViewModeChange(val); }}
                sx={{
                    height: 26,
                    bgcolor: 'rgba(0,0,0,0.05)',
                    borderRadius: '10px',
                    p: 0.35,
                    border: '1px solid rgba(0,0,0,0.08)',
                    gap: 0.3,
                    '& .MuiToggleButtonGroup-grouped': {
                        border: 'none !important',
                        borderRadius: '7px !important',
                        px: 1.25,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        gap: 0.5,
                        transition: 'all 0.18s ease',
                        '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: '#fff',
                            boxShadow: '0 1px 6px rgba(25,118,210,0.35)',
                            '&:hover': {bgcolor: 'primary.dark'},
                        },
                        '&:hover:not(.Mui-selected)': {bgcolor: 'rgba(255,255,255,0.7)'},
                    },
                }}
            >
                <Tooltip title="Vue tableau">
                    <ToggleButton value="table">
                        <TableChartIcon sx={{fontSize: '0.95rem'}}/>
                    </ToggleButton>
                </Tooltip>
                <Tooltip title="Vue carte">
                    <span style={{display: 'flex', alignItems: 'center'}}>
                        <ToggleButton value="map" disabled={!csvData}>
                            <MapIcon sx={{fontSize: '0.95rem'}}/>
                        </ToggleButton>
                    </span>
                </Tooltip>
            </ToggleButtonGroup>

            {/* ── Séparateurs CSV (menu compact) ───────────────────────────── */}
            <Tooltip title="Options de séparateurs pour l'export CSV">
                <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ExpandMoreIcon sx={{fontSize: '0.75rem !important', ml: -0.5}}/>}
                    onClick={e => setCsvMenuAnchor(e.currentTarget)}
                    sx={{
                        ...outlinedBtnSx,
                        gap: 0.5,
                        px: 1.2,
                        minWidth: 0,
                    }}
                >
                    <Typography sx={{
                        fontSize: '0.56rem', fontWeight: 800, color: 'inherit',
                        textTransform: 'uppercase', letterSpacing: '0.12em',
                    }}>
                        CSV
                    </Typography>
                    <Typography sx={{fontFamily: 'monospace', fontWeight: 800, fontSize: '0.82rem', color: 'inherit', lineHeight: 1}}>
                        {csvSeparator === '\\t' ? '⇥' : csvSeparator}
                    </Typography>
                </Button>
            </Tooltip>

            <Menu
                anchorEl={csvMenuAnchor}
                open={Boolean(csvMenuAnchor)}
                onClose={() => setCsvMenuAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                slotProps={{paper: {
                    elevation: 6,
                    sx: {
                        borderRadius: 2.5, mt: 0.5, minWidth: 230,
                        border: '1px solid rgba(25,118,210,0.12)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.13)',
                        p: 0,
                    },
                }}}
            >
                {/* ── Séparateur de colonne ── */}
                <Box sx={{px: 2, pt: 1.5, pb: 1}}>
                    <Typography sx={{
                        fontSize: '0.6rem', fontWeight: 800, color: 'text.disabled',
                        textTransform: 'uppercase', letterSpacing: '0.14em', mb: 1,
                    }}>
                        Séparateur de colonne
                    </Typography>
                    <Box sx={{display: 'flex', gap: 0.5}}>
                        {SEP_OPTIONS.map(({value, label, title}) => (
                            <Tooltip key={value} title={title} placement="bottom">
                                <Box
                                    onClick={() => setCsvSeparator(value)}
                                    sx={{
                                        width: 34, height: 30,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: 1.5,
                                        cursor: 'pointer',
                                        fontFamily: 'monospace',
                                        fontWeight: 800,
                                        fontSize: '0.82rem',
                                        transition: 'all 0.15s ease',
                                        bgcolor: csvSeparator === value ? 'primary.main' : 'rgba(0,0,0,0.04)',
                                        color: csvSeparator === value ? '#fff' : 'text.secondary',
                                        boxShadow: csvSeparator === value ? '0 2px 6px rgba(25,118,210,0.4)' : 'none',
                                        border: csvSeparator === value
                                            ? '1.5px solid transparent'
                                            : '1.5px solid rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            bgcolor: csvSeparator === value ? 'primary.dark' : 'rgba(25,118,210,0.1)',
                                            borderColor: 'primary.light',
                                        },
                                    }}
                                >
                                    {label}
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{opacity: 0.5}}/>

                {/* ── Séparateur de liste ── */}
                <Box sx={{px: 2, pt: 1, pb: 1.5}}>
                    <Typography sx={{
                        fontSize: '0.6rem', fontWeight: 800, color: 'text.disabled',
                        textTransform: 'uppercase', letterSpacing: '0.14em', mb: 1,
                    }}>
                        Séparateur de liste
                    </Typography>
                    <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                        {LIST_SEP_OPTIONS.map(({value, label, title}) => (
                            <Tooltip key={value} title={title} placement="bottom">
                                <Box
                                    onClick={() => setListSeparator(value)}
                                    sx={{
                                        minWidth: 34, height: 30, px: 0.75,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        borderRadius: 1.5,
                                        cursor: 'pointer',
                                        fontFamily: 'monospace',
                                        fontWeight: 800,
                                        fontSize: '0.75rem',
                                        transition: 'all 0.15s ease',
                                        bgcolor: listSeparator === value ? 'secondary.main' : 'rgba(0,0,0,0.04)',
                                        color: listSeparator === value ? '#fff' : 'text.secondary',
                                        boxShadow: listSeparator === value ? '0 2px 6px rgba(156,39,176,0.4)' : 'none',
                                        border: listSeparator === value
                                            ? '1.5px solid transparent'
                                            : '1.5px solid rgba(0,0,0,0.1)',
                                        '&:hover': {
                                            bgcolor: listSeparator === value ? 'secondary.dark' : 'rgba(156,39,176,0.1)',
                                            borderColor: 'secondary.light',
                                        },
                                    }}
                                >
                                    {label}
                                </Box>
                            </Tooltip>
                        ))}
                    </Box>

                </Box>

            </Menu>

        </Paper>

        {/* ── Dialog Enregistrer sous ───────────────────────────────────────── */}
        <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
                Enregistrer sous…
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus fullWidth size="small"
                    label="Nom de la configuration"
                    value={saveDialogName}
                    onChange={e => setSaveDialogName(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && saveDialogName.trim()) {
                            onSaveAs?.(saveDialogName.trim());
                            setSaveDialogOpen(false);
                        }
                    }}
                    sx={{ mt: 1 }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setSaveDialogOpen(false)} color="inherit" size="small">
                    Annuler
                </Button>
                <Button
                    variant="contained" size="small"
                    disabled={!saveDialogName.trim()}
                    startIcon={<SaveIcon sx={{ fontSize: '0.9rem !important' }} />}
                    onClick={() => {
                        onSaveAs?.(saveDialogName.trim());
                        setSaveDialogOpen(false);
                    }}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
}
